/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useMemo,
  useState
} from 'react';
import {makeStyles} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import {Groups, Link} from '@ion/core/src/types/entities';
import ClickAwayListener from '@ion/dls/src/components/listeners/ClickAwayListener';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import Render from '@ion/dls/src/render/Render';
import {dlsColor} from '@ion/dls/src/theme/theme';
import DeviceTooltipGroupDetails from './DeviceTooltipGroupDetails';
import IdentifierDialog from './IdentifierDialog';

interface DeviceDialogGroupDetailsType {
  setShowDialog?: (showDialog: boolean) => void;
  itemLength?: number;
  showDialog?: boolean;
  groupName?: string;
  deviceName?: string;
  groups?: Groups[];
  itemToDisplay?: Link[];
  moreItem?: number;
};

const useStyles = makeStyles(() => ({
  groups : {
    alignItems     : 'center',
    cursor         : 'pointer',
    width          : '100%',
    justifyContent : 'left',
    fontSize       : '10px',
    fontWeight     : 500
  },
  groupSection : {
    display : 'flex'
  },
  moreGroups : {
    color  : dlsColor('grey3'),
    cursor : 'pointer'
  }
}));

/**
 * Defines the DeviceDialogGroupDetails component. This component is used to
 * display the group names when we hover on groups.
 *
 * ```typescript
 *
 *   import DeviceDialogGroupDetails from './DeviceDialogGroupDetails';
 *
 *   <DeviceDialogGroupDetails  />
 *
 * ```
 */
const DeviceDialogGroupDetails = ({
  setShowDialog,
  itemLength,
  showDialog,
  itemToDisplay,
  groupName,
  deviceName,
  groups,
  moreItem
}: DeviceDialogGroupDetailsType) => {
  const classes                     = useStyles();
  const [openGroups, setOpenGroups] = useState(false);
  const remainingGroups             = groups?.slice?.(1, groups?.length);

  const toggleTooltip = useCallback((open = false) => {
    return function(e) {
      e.preventDefault();
      if (!open) {
        setOpenGroups(false);
      } else {
        setOpenGroups(true);
      }
    };
  }, [openGroups]);

  const moreGroupsText = useMemo(() => {
    return remainingGroups?.length >= 1 ? `${translate('admin-devices:grid.inheritableGroups', {groupLength: remainingGroups?.length})}` : '';
  }, [remainingGroups]);

  if (groups) {
    return (
      <>
        <ClickAwayListener onClickAway={toggleTooltip()}
          mountListener>
          <Tooltip arrow
            open={openGroups}
            onClose={toggleTooltip()}
            interactive={true}
            title={
              <DeviceTooltipGroupDetails itemToDisplay={itemToDisplay}
                groupLength={remainingGroups?.length}
                groupSections={classes.groups}
                setShowDialog={setShowDialog}
                itemLength={itemLength}
                moreItem={moreItem} />
            }
            placement='bottom'>
            <div className={classes.groupSection}>
              {groupName}
              {remainingGroups?.length >= 1 && (
                <div className={classes.moreGroups} onClick={toggleTooltip(true)}>
                  &nbsp;<span>{moreGroupsText}</span>
                </div>
              )}
            </div>
          </Tooltip>
        </ClickAwayListener>
        <Render.If condition={showDialog}>
          <IdentifierDialog setShowDialog={setShowDialog}
            showDialog={showDialog}
            deviceName={deviceName}
            data={groups} />
        </Render.If>
      </>
    );
  }
  return <div/>;
};

export default React.memo(DeviceDialogGroupDetails);
